import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
	AfterViewInit,
	ChangeDetectionStrategy,
	Component,
	computed,
	DestroyRef,
	effect,
	ElementRef,
	input,
	signal,
	viewChild
} from '@angular/core';
import { toSignal, toObservable, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DomSanitizer } from '@angular/platform-browser';
import { BehaviorSubject, combineLatest, distinctUntilChanged, filter, map, pairwise, switchMap, tap } from 'rxjs';

import { LightingSegmentMode, RgbColorMix } from '@shure/shared/angular/data-access/system-api/core';
import {
	colorToHex,
	colorToMatrix,
	DeviceModel,
	DeviceMuteControlFunction,
	LightBehavior,
	LightColor,
	LightingRingStyle,
	PolarPattern
} from '@shure/shared/angular/data-access/system-api/models';
import { LightErrorState } from '@shure/shared/angular/features/device-lights-settings/data-access';

import { LightsImageProvider } from './providers/lights-image.provider';
import { DeviceLightsBehaviorPluginService, DeviceLightsImageNextService } from './services';

const AUDIO_MUTE_CONTROL_COLORS = {
	Disabled: '#CCCCCC',
	Enabled: '#FFFFFF'
};

@Component({
	selector: 'sh-device-lights-image-next',
	imports: [CommonModule],
	providers: [DeviceLightsImageNextService, DeviceLightsBehaviorPluginService],
	templateUrl: './device-lights-image-next.component.html',
	styleUrl: './device-lights-image-next.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class DeviceLightsImageNextComponent implements AfterViewInit {
	public readonly container = viewChild('container', { read: ElementRef });
	public readonly deviceId = input.required<string>();
	public isLoading = signal<boolean>(true);

	private readonly reRender$ = new BehaviorSubject<string | undefined>(undefined);

	public readonly device = toSignal(
		combineLatest([this.reRender$, toObservable(this.deviceId)]).pipe(
			switchMap(([reId, deviceId]) => {
				return this.deviceLightsImageService.getDevice$(reId ?? deviceId);
			})
		)
	);

	// --- Memoized Computed Properties ---
	public readonly features = computed(() => this.device()?.features);
	public readonly lightingConstraints = computed(() => this.device()?.lightingConstraints);
	public readonly lightingCurrentBehavior = computed(() => this.features()?.lightingCurrentBehavior);
	public readonly lightingRingStyle = computed(() => this.features()?.lightingRingStyle);
	public readonly lightingSegmentsBehavior = computed(() => this.features()?.lightingSegmentsBehavior ?? []);
	public readonly errorIndicator = computed(() => this.features()?.errorIndicator.state !== LightErrorState.NoError);
	public readonly isIdentifying = computed(() => this.features()?.identify?.isIdentifying ?? false);
	public readonly audioMuteControlFunction = computed(() => this.features()?.audioMuteControl?.function);

	public readonly lightingMuteColor = computed(() => this.features()?.lightingMuteBehavior?.color);
	public readonly lightingUnmuteColor = computed(() => this.features()?.lightingUnmuteBehavior?.color);

	public readonly lightingBrightness = computed(() => {
		const f = this.features();
		const c = this.lightingConstraints();
		return f?.lightingBrightness && c ? f.lightingBrightness.level / c.lightingBrightness.max : 0;
	});

	public readonly lightingCurrentColor = computed(() => this.lightingCurrentBehavior()?.color);
	public readonly lightingCurrentMode = computed(() => this.lightingCurrentBehavior()?.mode);

	public readonly isLightingSegmentsStyle = computed(
		() => this.lightingRingStyle()?.style === LightingRingStyle.Segment
	);

	public readonly lightingSegmentsIsPulsing = computed(() => {
		const modes = this.lightingSegmentsBehavior().map((segment) => segment.mode);
		const segmentsModes = modes.filter((mode) => mode !== LightingSegmentMode.Off);
		return segmentsModes.length > 0 && segmentsModes.every((mode) => mode === LightingSegmentMode.Pulsing);
	});

	public readonly lightingSegmentsIsIdentifying = computed(() => {
		const channels = this.features()?.channels.filter((channel) => channel.features.identify);
		return (
			(channels?.some((channel) => channel.features.identify?.isIdentifying) && !this.isIdentifying()) ?? false
		);
	});

	public readonly hasToroidOrOmniPattern = computed(() => {
		const channels = this.features()?.channels.filter((channel) => channel.features.identify);
		return (
			channels?.some((channel) => {
				const polarPattern = channel.features.micPolarPattern?.pattern;
				return polarPattern === PolarPattern.Toroid || polarPattern === PolarPattern.Omnidirectional;
			}) ?? false
		);
	});

	public readonly disableLeds = computed(() =>
		this.isIdentifying() || this.errorIndicator() || this.lightingSegmentsIsIdentifying()
			? false
			: (this.lightingCurrentMode() === LightBehavior.Off && !this.isLightingSegmentsStyle()) ||
			  this.lightingBrightness() === 0
	);

	public readonly isFlashing = computed(
		() =>
			(this.isLightingSegmentsStyle() || this.lightingSegmentsIsIdentifying()
				? this.lightingSegmentsIsPulsing()
				: this.lightingCurrentMode() === LightBehavior.Flashing) || this.errorIndicator()
	);

	public readonly image = toSignal(
		toObservable(this.device).pipe(
			filter((device) => !!device?.model),
			distinctUntilChanged((prev, curr) => prev?.model === curr?.model),
			tap(() => this.isLoading.set(true)),
			switchMap((device) => {
				const imageUrl = this.imageProvider.getLightsImage(device?.model ?? DeviceModel.UNKNOWN)?.imageUrl;
				return this.http.get(`./assets/images/devices-lights/devices-next/${imageUrl}`, {
					responseType: 'text'
				});
			}),
			tap(() => this.isLoading.set(false)),
			map((image) => this.sanitizer.bypassSecurityTrustHtml(image))
		)
	);

	constructor(
		private readonly deviceLightsImageService: DeviceLightsImageNextService,
		private readonly imageProvider: LightsImageProvider,
		private readonly destroyRef: DestroyRef,
		private http: HttpClient,
		private sanitizer: DomSanitizer
	) {
		effect(() => this.updateLightingDisplay());
		effect(() => this.updateMuteLedColor());
	}

	public ngAfterViewInit(): void {
		//when mute/unmute behavior/color changed,  force to get device data
		this.deviceLightsImageService
			.getDevice$(this.deviceId())
			.pipe(pairwise(), takeUntilDestroyed(this.destroyRef))
			.subscribe(([prev, curr]) => {
				if (
					prev.features.lightingRingStyle?.style !== curr.features.lightingRingStyle?.style ||
					JSON.stringify(prev.features.lightingSegmentsBehavior) !==
						JSON.stringify(curr.features.lightingSegmentsBehavior)
				) {
					this.reRender$.next(curr.id);
				}
			});
	}

	// Update ring/segment visibility and colors based on device state
	private updateLightingDisplay(): void {
		const container = this.container()?.nativeElement;
		if (!container) return;

		const ringElem = <Element>container.querySelector('#sh-style-ring');
		const segmentsElem = <Element>container.querySelector('#sh-style-segments');

		const segmentMode =
			this.isLightingSegmentsStyle() || this.lightingSegmentsIsIdentifying() || this.hasToroidOrOmniPattern();

		const disableLeds = this.disableLeds();

		// Always hide both first to ensure a clean state
		ringElem?.setAttribute('visibility', 'hidden');
		segmentsElem?.setAttribute('visibility', 'hidden');

		if (segmentMode && !disableLeds) {
			this.setSegmentLedColors(container, segmentsElem);
			segmentsElem?.setAttribute('visibility', 'visible');
		} else {
			this.setLedColors(container);
			ringElem?.setAttribute('visibility', disableLeds ? 'hidden' : 'visible');
		}
	}

	// Determines when to set the mute LED colors based on the device's properties
	private updateMuteLedColor(): void {
		const container = this.container()?.nativeElement;
		if (!container) return;

		const shMuteLeds = <Element>container.querySelector('.sh-mute-group');
		const color =
			this.audioMuteControlFunction() === DeviceMuteControlFunction.Disabled
				? AUDIO_MUTE_CONTROL_COLORS.Disabled
				: AUDIO_MUTE_CONTROL_COLORS.Enabled;
		shMuteLeds?.setAttribute('fill', color);
	}

	// Set non-segmented LED colors and styles
	private setLedColors(container: Element): void {
		const shLeds = Array.from(container.querySelectorAll('.sh-led'));
		const shLedFilters = Array.from(container.querySelectorAll('.sh-led-filter'));

		const color = this.lightingCurrentColor();
		const lightingBrightness = this.lightingBrightness();

		if (this.errorIndicator()) {
			this.updateLedAttributes(shLeds, shLedFilters, LightColor.Red, 1);
		} else if (this.isIdentifying()) {
			this.updateLedAttributes(
				shLeds,
				shLedFilters,
				LightColor.Green,
				this.lightingSegmentsBehavior().length > 0 && lightingBrightness !== 0 ? lightingBrightness : 1
			);
		} else if (color === this.lightingMuteColor() || color === this.lightingUnmuteColor()) {
			this.updateLedAttributes(shLeds, shLedFilters, color ?? LightColor.Green, lightingBrightness);
		}
	}

	// Set segment LED colors and styles
	private setSegmentLedColors(container: Element, segmentsElem: Element): void {
		if (!segmentsElem) return;

		const segments = Array.from(segmentsElem.children);
		const segmentsBehavior = this.lightingSegmentsBehavior();
		if (!segmentsBehavior) return;

		const isFlashing = this.isFlashing();
		const isIdentifying = this.isIdentifying();
		const segmentCount = segmentsBehavior.length;
		const lightingBrightness = this.lightingBrightness();

		segments.forEach((segment, index) => {
			const behavior = segmentsBehavior[index];
			const color = behavior?.color;
			const mode = behavior?.mode;

			const segmentColor = this.getSegmentColorHex(color, mode, lightingBrightness);

			const nextIndex = (index + 1) % segmentCount;
			const prevIndex = (index - 1 + segmentCount) % segmentCount;

			const blendNextSegment = segmentsBehavior[nextIndex]?.mode !== LightingSegmentMode.Off;
			const blendPrevSegment = segmentsBehavior[prevIndex]?.mode !== LightingSegmentMode.Off;

			this.updateFadeSegment(container, index, segmentColor, blendPrevSegment, blendNextSegment);

			if (mode !== LightingSegmentMode.Off) {
				this.updateSegmentFilter(container, index, segmentColor);
			}

			this.toggleSegmentClass(segment, mode, isIdentifying, isFlashing);
		});
	}

	private updateLedAttributes(shLeds: Element[], shLedFilters: Element[], color: LightColor, alpha: number): void {
		const hex = colorToHex[color] + `${Math.floor(alpha * 255).toString(16)}`;
		for (const shLed of shLeds) {
			shLed.setAttribute('fill', hex);
		}
		for (const shLedFilter of shLedFilters) {
			shLedFilter?.setAttribute('values', colorToMatrix[color].replace('1 0', `${alpha} 0`));
		}
	}

	private getSegmentColorHex(
		color: RgbColorMix | undefined,
		mode: LightingSegmentMode | undefined,
		brightness: number
	): string {
		const baseColor = color ? this.rgbToHex(color) : colorToHex[LightColor.Green];
		const effectiveBrightness = mode === LightingSegmentMode.Flashing && brightness === 0 ? 1 : brightness;
		const alpha = mode !== LightingSegmentMode.Off ? Math.floor(effectiveBrightness * 255).toString(16) : '00';
		return baseColor + alpha;
	}

	private updateFadeSegment(
		container: Element,
		index: number,
		segmentColor: string,
		blendPrev: boolean,
		blendNext: boolean
	): void {
		const fadeSegment = <Element>container.querySelector(`#sh-fade-segment-${index + 1}`);
		if (!fadeSegment) return;

		Array.from(fadeSegment.children).forEach((child, childIdx) => {
			if (childIdx === 0) {
				child.setAttribute('stop-opacity', blendPrev ? '1' : '0');
			} else if (childIdx === 2) {
				child.setAttribute('stop-opacity', blendNext ? '1' : '0');
			}
			child.setAttribute('stop-color', segmentColor);
		});
	}

	private updateSegmentFilter(container: Element, index: number, segmentColor: string): void {
		const segmentFilter = <Element>container.querySelector(`#sh-segment-filter-${index + 1}`);
		segmentFilter?.setAttribute('flood-color', segmentColor);
	}

	private toggleSegmentClass(
		segment: Element,
		mode: LightingSegmentMode | undefined,
		isIdentifying: boolean,
		isFlashing: boolean
	): void {
		this.toggleClass(segment, 'sh-segment-identify', mode === LightingSegmentMode.Flashing && !isIdentifying);
		this.toggleClass(segment, 'sh-segment-pulse', mode === LightingSegmentMode.Pulsing && !isFlashing);
	}

	private toggleClass(element: Element, className: string, shouldHave: boolean): void {
		if (shouldHave) {
			if (!element.classList.contains(className)) {
				requestAnimationFrame(() => {
					element.classList.add(className);
				});
			} else {
				element.classList.remove(className);
				requestAnimationFrame(() => {
					element.classList.add(className);
				}); // requestAnimationFrame to allow the browser to reset the animation
			}
		} else {
			if (element.classList.contains(className)) {
				element.classList.remove(className);
			}
		}
	}

	private rgbToHex(color: RgbColorMix): string {
		const r = color.red.toString(16).padStart(2, '0');
		const g = color.green.toString(16).padStart(2, '0');
		const b = color.blue.toString(16).padStart(2, '0');
		return `#${r}${g}${b}`;
	}
}
